import React from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Stack from '@mui/material/Stack';
import Tooltip from '@mui/material/Tooltip';
import { useTranslation } from 'react-i18next';
import { DownArrow, InfoIcon } from '@common/utils/icons/icons';
import DeploymentWeeks from '../../models/organization/deployment-week';
import CustomTable, { renderConnectionCell } from '../misc/CustomTable';
import { GridColDef, GridRowSelectionModel } from '@mui/x-data-grid';
import HttpFetchStatus from '@common/enums/api/http-fetch-status';
import { DateTime } from 'luxon';
import DateInput from '@common/components/Inputs/DateInput';
import SwitchInput from '../forms/SwitchInput';
import Robot from '../../models/robots/robot';

interface IWeekFilters {
  weekFilter: { start: DateTime | null; end: DateTime | null };
  onWeekFilterChange: (weekFilter: Partial<{ start: DateTime; end: DateTime }>) => void;
}

interface IRobotDeployment {
  getStatus: HttpFetchStatus;
  deploymentWeeks: DeploymentWeeks[] | null;
  filteredRobots: string[] | null;
  onUpdateFilteredRobots: (ids: string[] | null) => void;
}

interface IAllToggle {
  all: boolean;
  onToggle: () => void;
}

interface IEndedDeploymentsToggle {
  showEndedDeployments: boolean;
  onToggle: () => void;
}

const FiltersDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  onResetFilter: () => void;
  weekFiltersHandler?: IWeekFilters;
  robotDeploymentHandler?: IRobotDeployment;
  allHandler?: IAllToggle;
  endedDeploymentsHandler?: IEndedDeploymentsToggle;
  robotsData?: Robot[] | null;
}> = ({
  open,
  onClose,
  onResetFilter,
  weekFiltersHandler,
  robotDeploymentHandler,
  allHandler,
  endedDeploymentsHandler,
  robotsData,
}) => {
  const { t } = useTranslation();

  const handleRobotSelectionChange = (selectionModel: GridRowSelectionModel) => {
    const newSelectedIds = selectionModel as string[];
    if (robotDeploymentHandler?.onUpdateFilteredRobots) {
      if (newSelectedIds.length === 0) {
        robotDeploymentHandler.onUpdateFilteredRobots(null);
      } else {
        robotDeploymentHandler.onUpdateFilteredRobots(newSelectedIds);
      }
    }
  };

  const columns: GridColDef[] = [
    {
      field: 'name',
      headerName: t('table-header-name') as string,
      width: 250,
    },
    {
      field: 'serialNumber',
      headerName: t('table-header-serial-number') as string,
      width: 200,
    },
    {
      field: 'isActive',
      headerName: t('table-header-active') as string,
      renderCell: renderConnectionCell,
      width: 100,
    },
  ];

  const startFilterChangeHandler = (start: DateTime | null) => {
    if (start && weekFiltersHandler) {
      weekFiltersHandler.onWeekFilterChange({ start });
    }
  };

  const endFilterChangeHandler = (end: DateTime | null) => {
    if (end && weekFiltersHandler) {
      weekFiltersHandler.onWeekFilterChange({ end });
    }
  };

  return (
    <React.Fragment>
      <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm">
        <DialogTitle>{t('title-filters')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2}>
            {weekFiltersHandler && (
              <Stack direction="row" spacing={2}>
                <DateInput
                  date={weekFiltersHandler.weekFilter.start}
                  title={t('input-label-week-filter-start')}
                  dateValidation={{ isValid: true, error: null }}
                  onDateTimeChange={startFilterChangeHandler}
                  maxDate={DateTime.now().endOf('week')}
                />
                <DateInput
                  date={weekFiltersHandler.weekFilter.end}
                  title={t('input-label-week-filter-end')}
                  dateValidation={{ isValid: true, error: null }}
                  onDateTimeChange={endFilterChangeHandler}
                  maxDate={DateTime.now().endOf('week')}
                />
              </Stack>
            )}
            {robotDeploymentHandler && (
              <Accordion disableGutters>
                <AccordionSummary expandIcon={<DownArrow />}>
                  <Stack
                    direction="row"
                    alignItems="center"
                    justifyContent="space-between"
                    width="100%"
                    mr={1}
                  >
                    {t('title-robots')}
                    <Tooltip title={t('info-tooltip-select-robots-filter')}>
                      <InfoIcon color="action" />
                    </Tooltip>
                  </Stack>
                </AccordionSummary>
                <AccordionDetails>
                  <CustomTable
                    getStatus={robotDeploymentHandler.getStatus}
                    hideFooter
                    checkboxSelection
                    autoHeight
                    dataLoaded={robotDeploymentHandler.deploymentWeeks != null}
                    failedToLoadMessage={t('error-loading-deployment-weeks')}
                    emptyDataMessage={t('info-no-deployment-weeks')}
                    data={robotsData ?? []}
                    columns={columns}
                    rowSelectionModel={robotDeploymentHandler.filteredRobots ?? []}
                    onRowSelectionModelChange={handleRobotSelectionChange}
                  />
                </AccordionDetails>
              </Accordion>
            )}
            {allHandler && (
              <SwitchInput
                label={t('input-label-fetch-all')}
                isChecked={allHandler.all}
                onChange={allHandler.onToggle}
              />
            )}
            {endedDeploymentsHandler && (
              <SwitchInput
                label={t('title-ended-deployments')}
                isChecked={endedDeploymentsHandler.showEndedDeployments}
                onChange={endedDeploymentsHandler.onToggle}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onResetFilter}>{t('button-reset')}</Button>
          <Button onClick={onClose}>{t('button-close')}</Button>
        </DialogActions>
      </Dialog>
    </React.Fragment>
  );
};

export default FiltersDialog;
